import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  IconButton,
  toast
} from '@sparrowengg/twigs-react';
import {
  CopyIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@sparrowengg/twigs-react-icons';
import ExternalLink from 'commons/icons/ExternalLink';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('get-started');
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    'install-node': false,
    'install-cursor': false,
    'install-ngrok': false,
    'create-app': false,
    'setup-ngrok': false,
    'create-mcp-dir': false,
    'generate-mcp': false,
    'initial-setup': false,
    'full-page-app': false,
    'contact-import': false,
    'response-import': false
  });

  const navigationItems = [
    { id: 'get-started', label: 'Get Started' },
    { id: 'start-coding', label: 'Start Coding' },
    { id: 'start-server', label: 'Start Server' },
    { id: 'location', label: 'Location' },
    { id: 'prompt-library', label: 'Prompt Library' }
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const CollapsibleSection = ({
    id,
    title,
    children,
    defaultExpanded = false
  }: {
    id: string;
    title: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
  }) => {
    const isExpanded = expandedSections[id] ?? defaultExpanded;

    return (
      <Box
        css={{
          marginBottom: '$8',
          border: '1px solid #e1e4e8',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '$white900',
          transition: 'all 0.2s ease'
        }}
      >
        <Flex
          onClick={() => toggleSection(id)}
          css={{
            padding: '$4 $6',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isExpanded ? '#f6f8fa' : 'transparent',
            borderBottom: isExpanded ? '1px solid #e1e4e8' : 'none',
            '&:hover': {
              backgroundColor: '#f6f8fa'
            },
            transition: 'background-color 0.2s ease'
          }}
        >
          <Heading
            size="h3"
            css={{
              color: '$neutral900',
              fontWeight: 600,
              fontSize: '18px',
              margin: 0
            }}
          >
            {title}
          </Heading>
          <Box
            css={{
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronDownIcon size={20} color="#6a737d" />
          </Box>
        </Flex>
        {isExpanded && (
          <Box
            css={{
              padding: '$6',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-10px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              },
              animation: 'fadeIn 0.2s ease'
            }}
          >
            {children}
          </Box>
        )}
      </Box>
    );
  };

  const CodeBlock = ({ children }: { children: React.ReactNode }) => {
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string') {
        return node;
      }
      if (typeof node === 'number') {
        return String(node);
      }
      if (Array.isArray(node)) {
        return node.map(extractText).join('');
      }
      if (node && typeof node === 'object' && 'props' in node) {
        return extractText(node.props.children);
      }
      return String(node);
    };

    const codeText = extractText(children);

    const handleCopy = () => {
      navigator.clipboard.writeText(codeText);
      toast({
        variant: 'success',
        description: 'Code copied to clipboard'
      });
    };

    return (
      <Box
        css={{
          position: 'relative',
          backgroundColor: '#f6f8fa',
          border: '1px solid #e1e4e8',
          borderRadius: '6px',
          padding: '$4 $5',
          marginTop: '$4',
          marginBottom: '$4',
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          fontSize: '13px',
          lineHeight: 1.45,
          color: '#24292e',
          overflowX: 'auto',
          '& pre': {
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontStyle: 'italic'
          }
        }}
      >
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
          {children}
        </pre>
        <IconButton
          icon={<CopyIcon />}
          size="sm"
          variant="ghost"
          color="default"
          onClick={handleCopy}
          css={{
            position: 'absolute',
            top: '$2',
            right: '$2',
            backgroundColor: '$white900',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: '$neutral100'
            }
          }}
        />
      </Box>
    );
  };

  const renderGetStarted = () => (
    <Box>
      <Heading
        size="h2"
        css={{
          marginBottom: '$6',
          color: '$neutral900',
          fontWeight: 600,
          fontSize: '28px'
        }}
      >
        Get Started
      </Heading>
      <Text
        size="md"
        css={{ color: '$neutral700', marginBottom: '$10', lineHeight: 1.7 }}
      >
        Follow these steps to set up your development environment for AppNest.
      </Text>

      <CollapsibleSection id="install-node" title="Install Node.js 22">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Node.js 22 is required for AppNest development. We recommend using NVM
          (Node Version Manager) to manage Node.js versions.
        </Text>

        <Box css={{ marginTop: '$8' }}>
          <Heading
            size="h4"
            css={{
              marginBottom: '$4',
              color: '$neutral900',
              fontWeight: 600,
              fontSize: '18px'
            }}
          >
            For macOS:
          </Heading>
          <Text
            size="md"
            css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
          >
            1. Install Homebrew (if not already installed):
          </Text>
          <CodeBlock>
            /bin/bash -c "$(curl -fsSL
            https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"
          </CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            2. Install NVM using Homebrew:
          </Text>
          <CodeBlock>brew install nvm</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            3. Add NVM to your shell profile (~/.zshrc or ~/.bash_profile):
          </Text>
          <CodeBlock>
            {`export NVM_DIR="$HOME/.nvm"
[ -s "$(brew --prefix)/opt/nvm/nvm.sh" ] && \\. "$(brew --prefix)/opt/nvm/nvm.sh"`}
          </CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            4. Reload your shell configuration:
          </Text>
          <CodeBlock>source ~/.zshrc</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            5. Install Node.js 22:
          </Text>
          <CodeBlock>nvm install 22</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            6. Set Node.js 22 as the default version:
          </Text>
          <CodeBlock>nvm use 22 && nvm alias default 22</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            7. Verify installation:
          </Text>
          <CodeBlock>node --version</CodeBlock>
        </Box>

        <Box css={{ marginTop: '$10' }}>
          <Heading
            size="h4"
            css={{
              marginBottom: '$4',
              color: '$neutral900',
              fontWeight: 600,
              fontSize: '18px'
            }}
          >
            For Windows:
          </Heading>
          <Text
            size="md"
            css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
          >
            1. Download NVM for Windows from:
          </Text>
          <Flex
            alignItems="center"
            gap="$2"
            css={{
              marginBottom: '$6',
              cursor: 'pointer',
              display: 'inline-flex'
            }}
            onClick={() =>
              window.open(
                'https://github.com/coreybutler/nvm-windows/releases',
                '_blank'
              )
            }
          >
            <Text
              size="md"
              css={{
                color: '$primary600',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              https://github.com/coreybutler/nvm-windows/releases
            </Text>
            <Box
              css={{
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '$2'
              }}
            >
              <ExternalLink />
            </Box>
          </Flex>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            2. Download and run the installer (nvm-setup.exe)
          </Text>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            3. Open a new Command Prompt or PowerShell window as Administrator
          </Text>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            4. Install Node.js 22:
          </Text>
          <CodeBlock>nvm install 22</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            5. Set Node.js 22 as the active version:
          </Text>
          <CodeBlock>nvm use 22</CodeBlock>

          <Text
            size="md"
            css={{
              color: '$neutral700',
              marginTop: '$6',
              marginBottom: '$3',
              lineHeight: 1.7
            }}
          >
            6. Verify installation:
          </Text>
          <CodeBlock>node --version</CodeBlock>
        </Box>

        <Box
          css={{
            marginTop: '$8',
            padding: '$4 $5',
            backgroundColor: '#f6f8fa',
            borderRadius: '6px',
            borderLeft: '3px solid #0366d6'
          }}
        >
          <Text size="sm" css={{ color: '$neutral700', lineHeight: 1.6 }}>
            <Text as="span" css={{ fontWeight: 600 }}>
              Installation URL:
            </Text>{' '}
            <Text
              as="span"
              css={{
                color: '$primary600',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={() => window.open('https://nodejs.org/', '_blank')}
            >
              https://nodejs.org/
            </Text>
          </Text>
        </Box>
      </CollapsibleSection>

      <CollapsibleSection id="install-cursor" title="Install Cursor">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Cursor is an AI-powered code editor that enhances your development
          experience with AppNest.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          1. Download Cursor from the official website:
        </Text>
        <Flex
          alignItems="center"
          gap="$2"
          css={{
            marginBottom: '$6',
            cursor: 'pointer',
            display: 'inline-flex'
          }}
          onClick={() => window.open('https://cursor.sh/', '_blank')}
        >
          <Text
            size="md"
            css={{
              color: '$primary600',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            https://cursor.sh/
          </Text>
          <Box
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: '$2'
            }}
          >
            <ExternalLink />
          </Box>
        </Flex>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          2. Install Cursor following the installation wizard for your operating
          system
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          3. Launch Cursor and sign in or create an account
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          4. Cursor is now ready to use with AppNest!
        </Text>
      </CollapsibleSection>

      <CollapsibleSection id="install-ngrok" title="Install ngrok">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          ngrok allows you to expose your localhost to the internet securely,
          which is essential for local AppNest development.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          1. Sign up for a free ngrok account at:
        </Text>
        <Flex
          alignItems="center"
          gap="$2"
          css={{
            marginBottom: '$6',
            cursor: 'pointer',
            display: 'inline-flex'
          }}
          onClick={() => window.open('https://ngrok.com/', '_blank')}
        >
          <Text
            size="md"
            css={{
              color: '$primary600',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            https://ngrok.com/
          </Text>
          <Box
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: '$2'
            }}
          >
            <ExternalLink />
          </Box>
        </Flex>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          2. Download ngrok for your operating system
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          3. Extract the ngrok executable to a location in your PATH (e.g.,
          /usr/local/bin on macOS or C:\\Program Files\\ngrok on Windows)
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          4. Get your authtoken from the ngrok dashboard and configure it:
        </Text>
        <CodeBlock>ngrok config add-authtoken YOUR_AUTHTOKEN</CodeBlock>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          5. Verify installation:
        </Text>
        <CodeBlock>ngrok version</CodeBlock>
      </CollapsibleSection>
    </Box>
  );

  const renderStartCoding = () => (
    <Box>
      <Heading
        size="h2"
        css={{
          marginBottom: '$6',
          color: '$neutral900',
          fontWeight: 600,
          fontSize: '28px'
        }}
      >
        Start Coding
      </Heading>
      <Text
        size="md"
        css={{ color: '$neutral700', marginBottom: '$10', lineHeight: 1.7 }}
      >
        Follow these steps to create and configure your first local development
        app.
      </Text>

      <CollapsibleSection id="create-app" title="Create a New App">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Click "Create new app" in the dashboard, then select "Configure local
          dev app" to set up your local development environment.
        </Text>
        <Box
          css={{
            marginTop: '$6',
            marginBottom: '$6',
            padding: '$4',
            backgroundColor: '#f6f8fa',
            borderRadius: '6px',
            border: '1px solid #e1e4e8',
            textAlign: 'center'
          }}
        >
          <img
            src="https://appnest-app.salesparrow.com/sdk-developer-portal-images/dev-local-app.png"
            alt="Configure Local Dev App"
            style={{
              width: '100%',
              maxWidth: '600px',
              borderRadius: '6px'
            }}
          />
        </Box>
        <Box
          css={{
            marginTop: '$6',
            padding: '$4 $5',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            borderLeft: '3px solid #ffc107'
          }}
        >
          <Text size="sm" css={{ color: '#856404', lineHeight: 1.6 }}>
            💡{' '}
            <Text as="span" css={{ fontWeight: 600 }}>
              Tip:
            </Text>{' '}
            Make sure you have all the required information ready before
            starting the configuration process.
          </Text>
        </Box>
      </CollapsibleSection>

      <CollapsibleSection id="setup-ngrok" title="Set Up ngrok">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Map ngrok to your localhost:3000 to expose your local server to the
          internet.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          1. Open a terminal window
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          2. Start ngrok to tunnel to localhost:3000:
        </Text>
        <CodeBlock>ngrok http 3000</CodeBlock>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          3. Copy the forwarding URL (e.g., https://abc123.ngrok.io) from the
          ngrok terminal output
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          4. Use this URL when configuring your local dev app in AppNest
        </Text>

        <Box
          css={{
            marginTop: '$6',
            padding: '$4 $5',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            borderLeft: '3px solid #ffc107'
          }}
        >
          <Text size="sm" css={{ color: '#856404', lineHeight: 1.6 }}>
            ⚠️{' '}
            <Text as="span" css={{ fontWeight: 600 }}>
              Important:
            </Text>{' '}
            Keep the ngrok terminal window open while developing. The URL will
            change if you restart ngrok.
          </Text>
        </Box>
      </CollapsibleSection>

      <CollapsibleSection
        id="create-mcp-dir"
        title="Create MCP Configuration Directory"
      >
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Before generating the MCP configuration, you need to create the
          necessary directory structure in your project root.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          Run the following command in your project root directory:
        </Text>
        <CodeBlock>{`mkdir -p .cursor && echo '{}' > .cursor/mcp.json`}</CodeBlock>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          This command creates the <code>.cursor</code> directory (if it
          doesn&apos;t exist) and initializes an empty MCP configuration file.
          The <code>mkdir -p</code> flag ensures the directory is created only
          if it doesn&apos;t already exist, preventing errors if the directory
          is already present.
        </Text>
      </CollapsibleSection>

      <CollapsibleSection id="generate-mcp" title="Generate MCP Configuration">
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Generate and configure the MCP (Model Context Protocol) configuration
          file for your project.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          1. In the app configuration page, click "Generate MCP configuration"
        </Text>

        <Box
          css={{
            marginTop: '$6',
            marginBottom: '$6',
            padding: '$4',
            backgroundColor: '#f6f8fa',
            borderRadius: '6px',
            border: '1px solid #e1e4e8',
            textAlign: 'center'
          }}
        >
          <img
            src="https://appnest-app.salesparrow.com/sdk-developer-portal-images/MCP-configuration.png"
            alt="Generate MCP Configuration"
            style={{
              width: '100%',
              maxWidth: '600px',
              borderRadius: '6px'
            }}
          />
        </Box>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          2. Copy the generated configuration JSON
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          3. In your project root directory, create a new file named
          `.cursor.mcp.json`
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          4. Paste the configuration into this file and save it
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          5. Restart Cursor to load the MCP configuration
        </Text>
      </CollapsibleSection>

      <CollapsibleSection
        id="initial-setup"
        title="Initial Setup with Base Prompt"
      >
        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$6', lineHeight: 1.7 }}
        >
          Use the base prompt to initialize your app with the necessary setup
          code.
        </Text>

        <Text
          size="md"
          css={{ color: '$neutral700', marginBottom: '$3', lineHeight: 1.7 }}
        >
          1. Open Cursor in your project directory
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          2. Use the following base prompt in Cursor's AI chat:
        </Text>
        <CodeBlock>Hi AppNest do the initial setup</CodeBlock>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          3. The AI will generate the base code structure and initial setup for
          your app
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          4. Review the generated code and make any necessary adjustments
        </Text>

        <Text
          size="md"
          css={{
            color: '$neutral700',
            marginTop: '$6',
            marginBottom: '$3',
            lineHeight: 1.7
          }}
        >
          5. Once the base setup is complete, you can start writing your
          business logic
        </Text>
      </CollapsibleSection>
    </Box>
  );

  const renderStartServer = () => (
    <Box>
      <Heading
        size="h2"
        css={{
          marginBottom: '$6',
          color: '$neutral900',
          fontWeight: 600,
          fontSize: '28px'
        }}
      >
        Start Server
      </Heading>
      <Text
        size="md"
        css={{ color: '$neutral700', marginBottom: '$10', lineHeight: 1.7 }}
      >
        Start your local development server to test your AppNest application.
      </Text>

      <Box
        css={{
          backgroundColor: '#f6f8fa',
          border: '1px solid #e1e4e8',
          borderRadius: '6px',
          padding: '$6',
          marginTop: '$4',
          marginBottom: '$6'
        }}
      >
        <Text
          size="sm"
          css={{
            color: '$neutral700',
            marginBottom: '$4',
            fontWeight: 600
          }}
        >
          Run the following command in your project directory:
        </Text>
        <CodeBlock>appnest-engine run run-all</CodeBlock>
      </Box>

      <Text
        size="md"
        css={{ color: '$neutral700', marginTop: '$6', lineHeight: 1.7 }}
      >
        The server will start and be available at{' '}
        <Text
          as="span"
          css={{
            fontWeight: 600,
            color: '$primary700',
            fontFamily: 'monospace'
          }}
        >
          http://localhost:3000
        </Text>
      </Text>

      <Box
        css={{
          marginTop: '$8',
          padding: '$4 $5',
          backgroundColor: '#d1ecf1',
          borderRadius: '6px',
          borderLeft: '3px solid #0c5460'
        }}
      >
        <Text size="sm" css={{ color: '#0c5460', lineHeight: 1.6 }}>
          Your server is now running! Make sure ngrok is also running and
          pointing to localhost:3000.
        </Text>
      </Box>
    </Box>
  );

  const renderLocation = () => (
    <Box>
      <Heading
        size="h2"
        css={{
          marginBottom: '$6',
          color: '$neutral900',
          fontWeight: 600,
          fontSize: '28px'
        }}
      >
        Location
      </Heading>
      <Text
        size="md"
        css={{ color: '$neutral700', marginBottom: '$10', lineHeight: 1.7 }}
      >
        AppNest apps can be placed in three different locations within Survey
        Sparrow, each serving a specific purpose.
      </Text>

      <Flex flexDirection="column" gap="$6">
        <CollapsibleSection id="full-page-app" title="1. Full Page App">
          <Text
            size="md"
            css={{ color: '$neutral700', lineHeight: 1.7, marginBottom: '$4' }}
          >
            Full page apps are displayed as standalone pages within Survey
            Sparrow. They provide a complete, dedicated interface for your
            application functionality.
          </Text>
          <Box
            css={{
              marginTop: '$6',
              marginBottom: '$6',
              padding: '$4',
              backgroundColor: '#f6f8fa',
              borderRadius: '6px',
              border: '1px solid #e1e4e8',
              textAlign: 'center'
            }}
          >
            <img
              src="https://appnest-app.salesparrow.com/sdk-developer-portal-images/full-page-app.png"
              alt="Full Page App Location"
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '6px'
              }}
            />
          </Box>
          <Text
            size="sm"
            css={{ color: '$neutral600', fontStyle: 'italic', lineHeight: 1.6 }}
          >
            <Text as="span" css={{ fontWeight: 600 }}>
              Use case:
            </Text>{' '}
            Custom dashboards, comprehensive tools, or full-featured
            applications that need their own dedicated space.
          </Text>
        </CollapsibleSection>

        <CollapsibleSection id="contact-import" title="2. Contact Import">
          <Text
            size="md"
            css={{ color: '$neutral700', lineHeight: 1.7, marginBottom: '$4' }}
          >
            Contact import apps appear in the contact import workflow, allowing
            users to import contacts from external sources or customize the
            import process.
          </Text>
          <Box
            css={{
              marginTop: '$6',
              marginBottom: '$6',
              padding: '$4',
              backgroundColor: '#f6f8fa',
              borderRadius: '6px',
              border: '1px solid #e1e4e8',
              textAlign: 'center'
            }}
          >
            <img
              src="https://appnest-app.salesparrow.com/sdk-developer-portal-images/contact-import.png"
              alt="Contact Import Location"
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '6px'
              }}
            />
          </Box>
          <Text
            size="sm"
            css={{ color: '$neutral600', fontStyle: 'italic', lineHeight: 1.6 }}
          >
            <Text as="span" css={{ fontWeight: 600 }}>
              Use case:
            </Text>{' '}
            Custom contact importers, data transformation tools, or integrations
            with external contact management systems.
          </Text>
        </CollapsibleSection>

        <CollapsibleSection id="response-import" title="3. Response Import">
          <Text
            size="md"
            css={{ color: '$neutral700', lineHeight: 1.7, marginBottom: '$4' }}
          >
            Response import apps are integrated into the response import
            workflow, enabling custom response data processing and import
            functionality.
          </Text>
          <Box
            css={{
              marginTop: '$6',
              marginBottom: '$6',
              padding: '$4',
              backgroundColor: '#f6f8fa',
              borderRadius: '6px',
              border: '1px solid #e1e4e8',
              textAlign: 'center'
            }}
          >
            <img
              src="https://appnest-app.salesparrow.com/sdk-developer-portal-images/response-import.png"
              alt="Response Import Location"
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '6px'
              }}
            />
          </Box>
          <Text
            size="sm"
            css={{ color: '$neutral600', fontStyle: 'italic', lineHeight: 1.6 }}
          >
            <Text as="span" css={{ fontWeight: 600 }}>
              Use case:
            </Text>{' '}
            Custom response importers, data validation tools, or integrations
            with external survey platforms.
          </Text>
        </CollapsibleSection>
      </Flex>

      <Box
        css={{
          marginTop: '$10',
          padding: '$4 $5',
          backgroundColor: '#f6f8fa',
          borderRadius: '6px',
          borderLeft: '3px solid #0366d6'
        }}
      >
        <Text size="sm" css={{ color: '$neutral700', lineHeight: 1.6 }}>
          💡{' '}
          <Text as="span" css={{ fontWeight: 600 }}>
            Tip:
          </Text>{' '}
          Choose the location that best fits your app's functionality. You can
          configure this during the app creation process.
        </Text>
      </Box>
    </Box>
  );

  const renderPromptLibrary = () => (
    <Box>
      <Heading
        size="h2"
        css={{
          marginBottom: '$6',
          color: '$neutral900',
          fontWeight: 600,
          fontSize: '28px'
        }}
      >
        Prompt Library
      </Heading>
      <Flex
        alignItems="center"
        justifyContent="center"
        css={{
          minHeight: '400px',
          flexDirection: 'column',
          gap: '$6',
          padding: '$12 $6'
        }}
      >
        <Box
          css={{
            padding: '$8',
            textAlign: 'center'
          }}
        >
          <Heading
            size="h3"
            css={{
              marginBottom: '$4',
              color: '$neutral900',
              fontWeight: 600,
              fontSize: '22px'
            }}
          >
            Coming Soon
          </Heading>
          <Text size="md" css={{ color: '$neutral600', lineHeight: 1.7 }}>
            We're working on a comprehensive prompt library to help you get
            started faster with AppNest development.
          </Text>
        </Box>
      </Flex>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'get-started':
        return renderGetStarted();
      case 'start-coding':
        return renderStartCoding();
      case 'start-server':
        return renderStartServer();
      case 'location':
        return renderLocation();
      case 'prompt-library':
        return renderPromptLibrary();
      default:
        return renderGetStarted();
    }
  };

  return (
    <Flex
      css={{
        height: 'calc(100vh - 80px)',
        overflow: 'hidden',
        backgroundColor: '$white900'
      }}
    >
      {/* Sidebar Navigation */}
      <Box
        css={{
          width: '260px',
          minWidth: '260px',
          height: '100%',
          borderRight: '1px solid #e1e4e8',
          backgroundColor: '#fafbfc',
          overflowY: 'auto',
          position: 'sticky',
          top: 0
        }}
      >
        <Box css={{ padding: '$6 $4' }}>
          <Heading
            size="h6"
            css={{
              color: '$neutral600',
              fontWeight: 600,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '$4',
              paddingLeft: '$3'
            }}
          >
            Documentation
          </Heading>
          <Flex flexDirection="column" gap="$1">
            {navigationItems.map((item) => (
              <Box
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                css={{
                  padding: '$3 $3',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor:
                    activeSection === item.id ? '#f6f8fa' : 'transparent',
                  borderLeft:
                    activeSection === item.id
                      ? '3px solid #d1d5db'
                      : '3px solid transparent',
                  '&:hover': {
                    backgroundColor:
                      activeSection === item.id ? '#f6f8fa' : '#f6f8fa'
                  }
                }}
              >
                <Text
                  size="sm"
                  css={{
                    color:
                      activeSection === item.id ? '$neutral900' : '$neutral700',
                    fontWeight: activeSection === item.id ? 600 : 400,
                    lineHeight: 1.5
                  }}
                >
                  {item.label}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        css={{
          flex: 1,
          overflowY: 'auto',
          padding: '$12 $16',
          backgroundColor: '$white900'
        }}
      >
        <Box css={{ maxWidth: '800px', margin: '0 auto' }}>
          {renderContent()}
        </Box>
      </Box>
    </Flex>
  );
};

export default Documentation;
